import crypto from 'crypto';

import _ from 'lodash';
import { z } from 'zod';

import { getLogger, Logger, stringify, Timer } from 'libs/logging-ts';
import { S3Resource } from 'libs/shared-types';
import {
  constructS3URL,
  doesFileExistInS3,
  downloadFileFromS3,
  uploadFileToS3,
} from 'libs/util-s3';

import {
  Speaker,
  SpeakerMap,
  TextToSpeechProvider,
  SpeakerData,
  getSpeakerListLocator,
  constructTTSOutputPath,
  constructTTSPreviewPath,
  TextToSpeechOptions,
  TTSACL,
} from './base';

import { ProviderConfiguration } from './configuration';
import { parseBuffer } from 'music-metadata';
import { TimedScript, VoiceOverTiming } from './timing';

export interface SpeakerPreviewLocator {
  location: S3Resource;
  url: string;
}

export const SpeakerSettingsSchema = z.record(z.string(), z.any());
export type SpeakerSettings = z.infer<typeof SpeakerSettingsSchema>;

export abstract class CachingTextToSpeechProvider implements TextToSpeechProvider {
  protected _logger: Logger;

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  protected async callProvider(
    text: string,
    speaker: Speaker,
    timeout: number,
    languageCode?: string,
    videoDuration?: number,
    voiceDirectionPrompt?: string,
  ): Promise<TimedScript> {
    throw new Error(
      'The callProvider method should be implemented in each CachingTextToSpeechProvider subclass.',
    );
  }

  constructor() {
    this._logger = getLogger({ metadata: { service: this.getName() } });
  }

  /**
   * This should return a SpeakerSettings instance containing all the settings that will be used
   * when converting text to speech with the given speaker: model, voice settings, etc. In addition
   * to use during text-to-speech, a hashed version of it is used in construction of S3 object names
   * used to store TTS output.
   *
   * @param speaker Speaker
   */
  getSpeakerSettings(speaker: Speaker): SpeakerSettings {
    throw new Error(
      'The getSpeakerSettings method should be implemented in each CachingTextToSpeechProvider subclass.',
    );
  }

  async getS3Speakers() {
    const logger = getLogger({
      metadata: { service: 'cachingTextToSpeechProvider.getS3Speakers' },
    });

    const s3Resource = getSpeakerListLocator();

    try {
      const speakers = await downloadFileFromS3(s3Resource);
      const speakerData = JSON.parse(speakers.toString());
      const speakerMap = new SpeakerMap(
        speakerData
          .filter((speaker: SpeakerData) => speaker.providerName === this.getName())
          .map((speaker: Speaker) => {
            speaker.textCharacterLimit = this.getTextCharacterLimit();
            return speaker;
          })
          .map((speaker: SpeakerData) => {
            return [speaker.id, new Speaker(speaker)];
          }, {}),
      );
      logger.debug('Retrieved S3 speakers', { speakerCount: speakerMap.asList().length });
      return speakerMap;
    } catch (error) {
      logger.error('Error getting S3 speakers. Falling back to hardcoded', { error });
      return this.defaultSpeakers();
    }
  }

  defaultSpeakers(): SpeakerMap {
    throw new Error(
      'The defaultSpeakers method should be implemented in each CachingTextToSpeechProvider subclass.',
    );
  }

  get logger(): Logger {
    return this._logger;
  }

  getConfiguration(): Promise<ProviderConfiguration> {
    throw new Error('Method not implemented.');
  }

  getName(): string {
    throw new Error(
      'The getName method should be implemented in each CachingTextToSpeechProvider subclass.',
    );
  }

  getSpeakerPreviewLocator(
    speaker: Speaker,
    isPaceText: boolean = false,
    languageCode?: string,
  ): SpeakerPreviewLocator {
    const text = isPaceText ? speaker.language.paceText : speaker.language.previewText;
    // For previews, don't include context parameters (languageCode, videoDuration, voiceDirectionPrompt)
    // This ensures consistent preview URLs. Language-specific providers (like Gemini) can override this method.
    const inputSignature = this.getTTSInputSignature(speaker, text);
    const filename = isPaceText ? `pace-${inputSignature}.mp3` : `preview-${inputSignature}.mp3`;

    const location = {
      bucket: this.getTextToSpeechBucket(),
      key: constructTTSPreviewPath(this.getName(), speaker.id, filename),
      region: process.env.AWS_REGION,
    };
    const url = constructS3URL(location);
    return { location, url };
  }

  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  async getSpeaker(speakerID: string) {
    const speakers = await this.getS3Speakers();
    const speaker = speakers.get(speakerID);
    if (speaker) {
      const speakerClone = _.cloneDeep(speaker);
      const previewLocator = this.getSpeakerPreviewLocator(speakerClone, false);
      const paceLocator = this.getSpeakerPreviewLocator(speakerClone, true);
      speakerClone.previewURL = previewLocator.url;
      speakerClone.paceURL = paceLocator.url;
      return speakerClone;
    }

    return null;
  }

  async getSpeakers(): Promise<SpeakerMap> {
    const s3Speakers = await this.getS3Speakers();
    const speakers = _.cloneDeep(s3Speakers);
    for (const speaker of speakers.values()) {
      const previewLocator = this.getSpeakerPreviewLocator(speaker, false);
      const paceLocator = this.getSpeakerPreviewLocator(speaker, true);
      speaker.previewURL = previewLocator.url;
      speaker.paceURL = paceLocator.url;
    }
    return speakers;
  }

  getTextCharacterLimit(): number {
    throw new Error(
      'The getTextCharacterLimit method should be implemented in each CachingTextToSpeechProvider subclass.',
    );
  }

  /**
   * This we get from the environment, not the configuration, because it's not provider-specific and
   * needs to be synchronized with the policies in the CloudFormation template.
   */
  getTextToSpeechBucket(): string {
    if (!process.env.ASSET_BUCKET) {
      throw new Error('No S3 bucket defined for text-to-speech cache.');
    }
    return process.env.ASSET_BUCKET;
  }

  /* eslint-disable-next-line @typescript-eslint/no-empty-function */
  async initialize(): Promise<void> {}

  async previewSpeaker(
    speaker: Speaker,
    timeout: number,
    overwrite: boolean = false,
    calculatePace: boolean = false,
  ): Promise<Speaker> {
    const { location, url } = this.getSpeakerPreviewLocator(speaker, calculatePace);

    const shouldCreatePreview = overwrite || (await doesFileExistInS3(location)) == false;
    let previewText: string;
    if (calculatePace) {
      speaker.paceURL = url;
      previewText = speaker.language.paceText;
    } else {
      speaker.previewURL = url;
      previewText = speaker.language.previewText;
    }
    if (shouldCreatePreview) {
      this.logger.debug('Creating new preview', { speaker, location });
      await this.textToSpeech({
        text: previewText,
        speaker,
        timeout,
        overwrite,
        acl: TTSACL.Public,
        location,
      });
    } else {
      this.logger.debug('Not overwriting existing preview.', { speaker, location });
    }
    const fileBuffer = await downloadFileFromS3(location);
    const metadata = await parseBuffer(fileBuffer);
    this.logger.info('Speaker preview created', {
      duration: metadata.format.duration,
      url,
      previewText,
    });
    if (calculatePace) {
      // Split the preview text on anything that's not a (possibly accented) letter or number.
      // This groups numbers as words, which seems closer to what we want than either counting
      // each digit as a word, or treating entire prices or phone numbers as one word.
      const words = previewText
        .split(/[^\p{Letter}\p{Mark}\p{Number}]+/u)
        .filter((text) => !text.match(/(^$|^[^\p{Letter}\p{Mark}\p{Number}]+$)/u));
      const wordCount = words.length;
      const pace = metadata?.format?.duration ? wordCount / metadata.format.duration : undefined;
      this.logger.info('Speaker pace calculated', {
        pace,
        previewText,
        speaker,
        wordCount,
        words,
        duration: metadata.format.duration,
      });
      if (pace) {
        speaker.pace = pace;
      }
    }
    return speaker;
  }

  async textToSpeech({
    text,
    speaker,
    timeout,
    overwrite = false,
    location,
    acl = TTSACL.Private,
    languageCode,
    videoDuration,
    voiceDirectionPrompt,
  }: TextToSpeechOptions): Promise<TimedScript> {
    const timer = new Timer(this.logger.configuration);
    let audio: Buffer;
    let timing: VoiceOverTiming;
    try {
      const audioS3Location =
        location ||
        this.getAudioS3Location(speaker, text, languageCode, videoDuration, voiceDirectionPrompt);
      const wordsS3Location = {
        ...audioS3Location,
        key: audioS3Location.key + '.timing.json',
      };

      if (overwrite === false) {
        // Check if both the audio and words files exist. We will want to redo the TTS if the words
        // have not been cached for old audio.
        const audioExists =
          (await doesFileExistInS3(audioS3Location)) && (await doesFileExistInS3(wordsS3Location));
        if (audioExists) {
          this.logger.info('Returning cached audio', {
            location: audioS3Location,
            languageCode,
            videoDuration,
          });
          audio = await downloadFileFromS3(audioS3Location);
          const wordsBuffer = await downloadFileFromS3(wordsS3Location);
          timing = JSON.parse(wordsBuffer.toString());
          return { audio, text, timing };
        } else {
          this.logger.info('Cache miss - will generate new audio', {
            audioExists,
            languageCode,
            videoDuration,
          });
        }
      }

      this.logger.info('Calling provider', {
        text: text,
        speakerID: speaker.id,
        languageCode,
        videoDuration,
        voiceDirectionPrompt,
      });
      const providerCall = new Timer(this.logger.configuration);
      ({ audio, text, timing } = await this.callProvider(
        text,
        speaker,
        timeout,
        languageCode,
        videoDuration,
        voiceDirectionPrompt,
      ));
      providerCall.info('callProvider completed');

      this.logger.debug('Caching audio', {
        text,
        speakerID: speaker.id,
        audioS3Location,
        acl,
      });
      await this.cacheAudio(audio, timing, audioS3Location, acl);
      return { audio, text, timing };
    } finally {
      timer.debug('textToSpeech finished');
    }
  }

  protected getAudioS3Location(
    speaker: Speaker,
    text: string,
    languageCode?: string,
    videoDuration?: number,
    voiceDirectionPrompt?: string,
  ): S3Resource {
    const bucket = this.getTextToSpeechBucket();
    const key = constructTTSOutputPath(
      this.getName(),
      speaker.id,
      this.getTTSInputSignature(speaker, text, languageCode, videoDuration, voiceDirectionPrompt) +
        '.mp3',
    );
    const audioS3Location = {
      bucket: bucket,
      key: key,
    };
    return audioS3Location;
  }

  /**
   * Hashes speaker settings and input text for use in a cache key.
   *
   * @param speaker Speaker
   * @param text The TTS input text.
   * @returns A hash of the speaker settings and input text.
   */
  private getTTSInputSignature(
    speaker: Speaker,
    text: string,
    languageCode?: string,
    videoDuration?: number,
    voiceDirectionPrompt?: string,
  ): string {
    const speakerSettings = this.getSpeakerSettings(speaker);
    // Hashing the stringified settings should pick up any variation that would affect generation:
    // model, voice settings, whatever. And safe-stable-stringify guarantees deterministic output
    // by controlling sorting, so changes to settings definitions shouldn't result in spurious
    // regeneration. Not that it's expensive enough to worry about.
    const settingsString = stringify(speakerSettings);

    // Include languageCode, videoDuration, and voiceDirectionPrompt in the cache key if any are defined
    // This ensures backward compatibility when all are undefined (preview URLs, old TTS calls)
    let hashInput = `${settingsString}-${text}`;

    if (
      languageCode !== undefined ||
      videoDuration !== undefined ||
      voiceDirectionPrompt !== undefined
    ) {
      const contextString = stringify({ languageCode, videoDuration, voiceDirectionPrompt });
      hashInput += `-${contextString}`;
    }

    return crypto.createHash('sha256').update(hashInput).digest('hex');
  }

  async cacheAudio(
    audio: Buffer,
    timing: VoiceOverTiming,
    location: S3Resource,
    acl: TTSACL = TTSACL.Private,
  ): Promise<void> {
    const timer = new Timer(this.logger.configuration);
    await Promise.all([
      uploadFileToS3({
        fileData: audio,
        key: location.key,
        bucket: location.bucket,
        acl,
      }),
      uploadFileToS3({
        fileData: JSON.stringify(timing),
        key: location.key + '.timing.json',
        bucket: location.bucket,
        acl,
      }),
    ]);

    timer.debug('cacheAudio finished');
  }
}
